/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * Minimal, dependency-free reimplementation of the handful of higher-order
 * component helpers that this codebase used from the unmaintained `recompose`
 * package. The behaviour of each helper matches recompose@0.30 so existing
 * components continue to work unchanged.
 */
import { Component, createElement } from 'react';

const mapValues = (obj, fn) => {
  const result = {};
  Object.keys(obj).forEach(key => {
    result[key] = fn(obj[key], key);
  });
  return result;
};

const getDisplayName = BaseComponent => {
  if (typeof BaseComponent === 'string') {
    return BaseComponent;
  }
  if (!BaseComponent) {
    return 'Component';
  }
  return BaseComponent.displayName || BaseComponent.name || 'Component';
};

const identity = Comp => Comp;

const noop = () => {};

/**
 * Performs right-to-left function composition, e.g.
 * compose(f, g, h)(x) === f(g(h(x))).
 */
export const compose = (...funcs) => {
  if (funcs.length === 0) {
    return arg => arg;
  }
  if (funcs.length === 1) {
    return funcs[0];
  }
  return funcs.reduce((a, b) => (...args) => a(b(...args)));
};

/**
 * Maps the incoming props to a new set of props before rendering BaseComponent.
 */
export const mapProps = propsMapper => BaseComponent => {
  const MapProps = props => createElement(BaseComponent, propsMapper(props));
  MapProps.displayName = `mapProps(${getDisplayName(BaseComponent)})`;
  return MapProps;
};

/**
 * Provides local reducer-driven state. Injects `stateName` (the current state)
 * and `dispatchName` (the dispatch function) as props. `initialState` may be a
 * plain value, a function of props, or omitted (in which case the reducer's own
 * default is used). Like recompose, `dispatch` accepts an optional second
 * `callback` argument that runs once the resulting state update has been
 * applied and receives the new state value.
 */
export const withReducer = (stateName, dispatchName, reducer, initialState) => BaseComponent => {
  class WithReducer extends Component {
    constructor(props) {
      super(props);
      let stateValue;
      if (initialState !== undefined) {
        stateValue = typeof initialState === 'function' ? initialState(props) : initialState;
      } else {
        stateValue = reducer(undefined, { type: '@@hoc/INIT' });
      }
      this.state = { stateValue };
      this.dispatch = this.dispatch.bind(this);
    }

    dispatch(action, callback = noop) {
      this.setState(
        ({ stateValue }) => ({ stateValue: reducer(stateValue, action) }),
        () => callback(this.state.stateValue)
      );
    }

    render() {
      return createElement(BaseComponent, {
        ...this.props,
        [stateName]: this.state.stateValue,
        [dispatchName]: this.dispatch,
      });
    }
  }

  WithReducer.displayName = `withReducer(${getDisplayName(BaseComponent)})`;
  return WithReducer;
};

/**
 * Injects stable handler props. `handlers` is either a map of
 * `props => (...args) => result` factories, or a function of props that returns
 * such a map. Each handler always sees the latest props at call time.
 */
export const withHandlers = handlers => BaseComponent => {
  class WithHandlers extends Component {
    constructor(props) {
      super(props);
      const handlerFactories = typeof handlers === 'function' ? handlers(props) : handlers;
      this.handlers = mapValues(handlerFactories, createHandler => (...args) =>
        createHandler(this.props)(...args)
      );
    }

    render() {
      return createElement(BaseComponent, { ...this.props, ...this.handlers });
    }
  }

  WithHandlers.displayName = `withHandlers(${getDisplayName(BaseComponent)})`;
  return WithHandlers;
};

/**
 * Provides local state together with updater functions. `initialState` may be a
 * plain value or a function of props. Each entry in `stateUpdaters` is a
 * `(state, props) => (...args) => partialState` factory; the returned partial
 * state is merged into the component state.
 *
 * The updater is applied via a functional `setState`, which React runs
 * asynchronously. React 16 pools SyntheticEvents and nullifies them after the
 * handler returns, so if the first argument looks like an event we call
 * `event.persist()` to keep it usable inside the deferred update (matching
 * recompose's behavior).
 */
export const withStateHandlers = (initialState, stateUpdaters) => BaseComponent => {
  class WithStateHandlers extends Component {
    constructor(props) {
      super(props);
      this.state = typeof initialState === 'function' ? initialState(props) : initialState;
      this.stateUpdaters = mapValues(stateUpdaters, handler => (maybeEvent, ...args) => {
        if (maybeEvent && typeof maybeEvent.persist === 'function') {
          maybeEvent.persist();
        }
        this.setState((state, props) => handler(state, props)(maybeEvent, ...args));
      });
    }

    render() {
      return createElement(BaseComponent, {
        ...this.props,
        ...this.state,
        ...this.stateUpdaters,
      });
    }
  }

  WithStateHandlers.displayName = `withStateHandlers(${getDisplayName(BaseComponent)})`;
  return WithStateHandlers;
};

/**
 * Returns a HOC that renders `RenderComponent` (with the incoming props)
 * instead of the wrapped BaseComponent.
 */
export const renderComponent = RenderComponent => () => {
  const Render = props => createElement(RenderComponent, props);
  Render.displayName = `renderComponent(${getDisplayName(RenderComponent)})`;
  return Render;
};

/**
 * Conditionally applies the `left` HOC when `test(props)` is truthy, otherwise
 * applies the `right` HOC (defaulting to identity).
 */
export const branch = (test, left, right = identity) => BaseComponent => {
  let leftFactory;
  let rightFactory;

  class Branch extends Component {
    render() {
      if (test(this.props)) {
        leftFactory = leftFactory || left(BaseComponent);
        return createElement(leftFactory, this.props);
      }
      rightFactory = rightFactory || right(BaseComponent);
      return createElement(rightFactory, this.props);
    }
  }

  Branch.displayName = `branch(${getDisplayName(BaseComponent)})`;
  return Branch;
};
